import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Confetti } from '@/components/ui/Confetti';
import {
  IconArrowRight,
  IconBookOpen,
  IconCheckCircle,
  IconLightbulb,
  IconSparkle,
} from '@/components/home/icons';

/**
 * /qari/onboarding/complete — terminal celebration step.
 *
 * Reached after /qari/onboarding/submitted (submission acknowledgement). Lands
 * with a one-shot confetti burst, then orients the new Qari toward the
 * dashboard and discovery surfaces.
 *
 * Visual language adapted from qari/final/DESIGN.md (Cyber-Creative)
 * using the existing night/beam palette already in tailwind.
 */
export function QariOnboardingFinal() {
  useDocumentTitle('Qari Application · Complete');

  return (
    <section className="relative overflow-hidden pb-24 pt-10 sm:pt-16">
      <Confetti />

      {/* Giant background watermarks (TRUST / VERIFY motif) */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -left-10 top-32 select-none font-display text-[160px] font-black leading-none tracking-tight text-night-50/[0.03] sm:text-[200px]"
      >
        TRUST
      </span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 bottom-20 select-none font-display text-[160px] font-black leading-none tracking-tight text-night-50/[0.03] sm:text-[200px]"
      >
        VERIFY
      </span>

      {/* Atmospheric glows */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-[-12%] top-1/4 h-96 w-96 rounded-full bg-beam/15 blur-[120px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-1/4 right-[-12%] h-80 w-80 rounded-full bg-beam-soft/15 blur-[120px]"
      />

      <div className="relative mx-auto w-full max-w-4xl px-4 sm:px-6">
        {/* 100% progress header */}
        <div className="mx-auto mb-12 max-w-3xl">
          <div className="mb-4 flex justify-between">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-beam">
              Onboarding Progress
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-beam">
              100% Complete
            </span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-night-500/40">
            <div className="h-full w-full rounded-full bg-beam shadow-[0_0_20px_-5px_rgba(0,229,255,0.6)]" />
          </div>
        </div>

        {/* Celebration card */}
        <div className="flex flex-col items-center rounded-2xl border border-night-500/40 bg-night-800/60 p-8 text-center backdrop-blur-md sm:p-16">
          <div className="relative mb-10 animate-home-float">
            <div
              aria-hidden="true"
              className="absolute inset-0 rounded-full bg-beam/20 blur-3xl"
            />
            <div className="relative flex h-32 w-32 items-center justify-center rounded-full border-2 border-beam bg-night-900 sm:h-44 sm:w-44">
              <IconCheckCircle className="h-20 w-20 text-beam sm:h-28 sm:w-28" />
              <span
                aria-hidden="true"
                className="absolute -right-2 -top-2 h-8 w-8 animate-pulse border-r-2 border-t-2 border-beam-soft"
              />
              <span
                aria-hidden="true"
                className="absolute -bottom-2 -left-2 h-8 w-8 animate-pulse border-b-2 border-l-2 border-beam-soft"
              />
            </div>
          </div>

          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-beam-soft">
            You're In
          </p>
          <h1 className="mb-5 font-display text-3xl font-extrabold tracking-tight text-beam sm:text-5xl">
            Congratulations, Qari
          </h1>
          <p className="mb-10 max-w-2xl leading-relaxed text-night-200">
            Your onboarding journey is complete. We're thrilled to welcome you to
            Alhudah's community of verified Quranic instructors. Your dashboard is
            ready — start shaping your teaching presence today.
          </p>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:gap-4">
            <Link
              to="/qari"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-beam px-10 py-3.5 text-xs font-semibold uppercase tracking-wide text-beam-ink transition-all hover:shadow-[0_0_24px_-4px_rgba(0,229,255,0.55)]"
            >
              Enter Dashboard
              <IconArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/reader"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-beam/50 px-10 py-3.5 text-xs font-semibold uppercase tracking-wide text-beam transition-colors hover:bg-beam/10"
            >
              Explore Alhudah
              <IconSparkle className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Next-step preview cards */}
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          <PreviewCard
            icon={<IconBookOpen className="h-5 w-5" />}
            title="Teaching Hub"
            body="Your curriculum and student roster appear here once your profile is approved."
          />
          <PreviewCard
            icon={<IconLightbulb className="h-5 w-5" />}
            title="Analytics"
            body="Track recitation progress and student engagement in one place."
          />
          <PreviewCard
            icon={<IconSparkle className="h-5 w-5" />}
            title="Preferences"
            body="Configure availability, teaching style, and session settings."
          />
        </div>
      </div>
    </section>
  );
}

function PreviewCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="group rounded-2xl border border-night-500/30 bg-night-800/60 p-5 backdrop-blur-md transition-colors hover:border-beam/60">
      <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-beam/10 text-beam transition-transform group-hover:scale-110">
        {icon}
      </span>
      <h3 className="mb-1 font-display text-base font-bold text-night-50">{title}</h3>
      <p className="text-sm leading-relaxed text-night-200">{body}</p>
    </div>
  );
}
